import {
    ImageEmbeddingSearchCandidate,
    ImageEmbeddingSearchHint,
    ImageEmbeddingSearchResponse,
    ImageEmbeddingSearchResultSection,
    LookitItemCategoryId,
    ProductCluster,
    ProductClusterType,
    ProductSubcategory,
    SourceStatus
} from './domain';
import { lookitProductClusters } from './mock-data';
import {
    rankSearchCandidate
} from './search-ranking-policy';

export interface VectorSearchCategoryFilter {
    selectedCategoryId: LookitItemCategoryId | string | null;
    categoryRoot?: string | null;
    subcategory?: ProductSubcategory | string | null;
    subcategoryConfirmed?: boolean;
    brandHints?: ImageEmbeddingSearchHint[];
    ocrHints?: ImageEmbeddingSearchHint[];
    wholesaleTagHints?: ImageEmbeddingSearchHint[];
    includeMismatchedCandidates?: boolean;
}

export interface UpsertProductVectorInput {
    vectorId: string;
    candidateProductId: string;
    categoryRootId?: string | null;
    categoryRoot?: string | null;
    subcategory?: ProductSubcategory | string | null;
    section?: ProductClusterType;
    visualScore?: number | null;
    brandScore?: number | null;
    textScore?: number | null;
    sellerPatternScore?: number | null;
    priceEvidenceScore?: number | null;
    matchReason?: string | null;
    sourceStatus?: SourceStatus;
}

export interface UpsertProductVectorResult {
    ok: true;
    vectorId: string;
    candidateProductId: string;
    sourceStatus: SourceStatus;
}

export interface DeleteVectorResult {
    ok: true;
    vectorId: string;
    status: 'deleted' | 'no_records';
    deletedAt: string;
}

export interface VectorSearchCandidate extends ImageEmbeddingSearchCandidate {
    queryVectorId: string;
    productVectorId: string;
    categoryMatched: boolean;
    subcategoryMatched: boolean | null;
}

export interface VectorSearchResultSection {
    section: ProductClusterType;
    label: string;
    candidates: VectorSearchCandidate[];
}

export interface VectorSearchResponse {
    vectorId: string;
    selectedCategoryId: LookitItemCategoryId | string | null;
    sourceStatus: SourceStatus;
    candidates: VectorSearchCandidate[];
    sections: VectorSearchResultSection[];
}

export interface VectorSearchRepository {
    searchSimilarProducts(
        vectorId: string,
        categoryFilter: VectorSearchCategoryFilter,
        limit?: number
    ): Promise<VectorSearchResponse>;
    upsertProductVector(input: UpsertProductVectorInput): Promise<UpsertProductVectorResult>;
    deleteVector(vectorId: string): Promise<DeleteVectorResult>;
}

interface StoredProductVector {
    vectorId: string;
    candidateProductId: string;
    categoryRootId: string | null;
    categoryRoot: string | null;
    subcategory: ProductSubcategory | string | null;
    section: ProductClusterType;
    visualScore: number | null;
    brandScore: number | null;
    textScore: number | null;
    sellerPatternScore: number | null;
    priceEvidenceScore: number | null;
    matchReason: string | null;
    sourceStatus: SourceStatus;
}

interface CategoryContext {
    selectedCategoryId: LookitItemCategoryId | string | null;
    categoryRoot: string | null;
    subcategory: ProductSubcategory | string | null;
    subcategoryConfirmed: boolean;
    brandHints: ImageEmbeddingSearchHint[];
    ocrHints: ImageEmbeddingSearchHint[];
    wholesaleTagHints: ImageEmbeddingSearchHint[];
    includeMismatchedCandidates: boolean;
}

interface CategoryScore {
    categoryScore: number;
    subcategoryScore: number;
    categoryMatched: boolean;
    subcategoryMatched: boolean | null;
}

const sectionOrder: ProductClusterType[] = ['brand_exact', 'wholesale_same', 'similar_style'];

const vectorIntentVisualScores: Record<string, Record<string, number>> = {
    top: {
        'cluster-liette-lace-blouse': 0.91,
        'cluster-similar-pintuck-blouse': 0.76,
        'cluster-muse-crop-cardigan': 0.58,
        'cluster-forest-zipup': 0.56,
        'cluster-muse-straight-denim': 0.34,
        'cluster-vegetable-cargo-pants': 0.32,
        'cluster-similar-wide-slacks': 0.31
    },
    bottom: {
        'cluster-muse-straight-denim': 0.92,
        'cluster-vegetable-cargo-pants': 0.87,
        'cluster-similar-wide-slacks': 0.75,
        'cluster-liette-lace-blouse': 0.33,
        'cluster-similar-pintuck-blouse': 0.32,
        'cluster-muse-crop-cardigan': 0.3,
        'cluster-forest-zipup': 0.3
    },
    shoes: {},
    bag: {}
};

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function clampScore(value: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(1, Math.round(value * 1000) / 1000));
}

function nonEmptyText(value?: string | null): string {
    return typeof value === 'string' ? value.trim() : '';
}

function sectionLabel(section: ProductClusterType): string {
    if (section === 'brand_exact') return '동일 상품 가능성 높음';
    if (section === 'wholesale_same') return '같은 도매택/동일 공급 상품 추정';
    return '유사 디자인 상품';
}

function categoryBaseId(categoryId?: string | null): string | null {
    const [baseId] = (categoryId || '').split(':');
    return baseId || null;
}

function categoryRootForId(categoryId?: string | null): string | null {
    const baseId = categoryBaseId(categoryId);
    if (baseId === 'top') return '상의';
    if (baseId === 'bottom') return '하의';
    if (baseId === 'outer') return '아우터';
    if (baseId === 'dress') return '원피스';
    if (baseId === 'shoes') return '신발';
    if (baseId === 'bag') return '가방';
    if (baseId === 'accessory') return '액세서리';
    return null;
}

function normalizeSubcategory(value?: string | null): ProductSubcategory | string | null {
    const normalizedValue = nonEmptyText(value).toLowerCase();
    if (!normalizedValue) return null;

    const aliases: Record<string, ProductSubcategory> = {
        pants: '팬츠',
        pant: '팬츠',
        trouser: '팬츠',
        trousers: '팬츠',
        slacks: '팬츠',
        '슬랙스': '팬츠',
        '바지': '팬츠',
        '팬츠': '팬츠',
        denim: '데님',
        jeans: '데님',
        jean: '데님',
        '청바지': '데님',
        '데님': '데님',
        skirt: '스커트',
        '스커트': '스커트',
        shorts: '쇼츠',
        short: '쇼츠',
        '쇼츠': '쇼츠',
        '반바지': '쇼츠',
        leggings: '레깅스',
        legging: '레깅스',
        '레깅스': '레깅스',
        blouse: '블라우스',
        '블라우스': '블라우스',
        tshirt: '티셔츠',
        't-shirt': '티셔츠',
        tee: '티셔츠',
        '티셔츠': '티셔츠',
        knit: '니트',
        '니트': '니트',
        shirt: '셔츠',
        '셔츠': '셔츠',
        cardigan: '가디건',
        '가디건': '가디건'
    };

    return aliases[normalizedValue] || value || null;
}

function subcategoryFromCategoryId(categoryId?: string | null): ProductSubcategory | string | null {
    const parts = (categoryId || '').split(':');
    return normalizeSubcategory(parts.length > 1 ? parts.slice(1).join(':') : null);
}

function compatibleExactSubcategories(subcategory?: ProductSubcategory | string | null): Set<string> | null {
    const normalized = normalizeSubcategory(subcategory);
    if (!normalized) return null;
    if (normalized === '팬츠' || normalized === '데님') return new Set(['팬츠', '데님']);
    return new Set([normalized]);
}

function normalizeFilter(filter: VectorSearchCategoryFilter): CategoryContext {
    const selectedCategoryId = filter.selectedCategoryId || null;
    const categoryRoot = filter.categoryRoot || categoryRootForId(selectedCategoryId);
    const subcategory = normalizeSubcategory(filter.subcategory || subcategoryFromCategoryId(selectedCategoryId));

    return {
        selectedCategoryId,
        categoryRoot,
        subcategory,
        subcategoryConfirmed: filter.subcategoryConfirmed ?? Boolean(subcategory),
        brandHints: filter.brandHints || [],
        ocrHints: filter.ocrHints || [],
        wholesaleTagHints: filter.wholesaleTagHints || [],
        includeMismatchedCandidates: filter.includeMismatchedCandidates || false
    };
}

function clusterById(productClusterId: string): ProductCluster | null {
    return lookitProductClusters.find((cluster) => cluster.id === productClusterId) || null;
}

function defaultRecordForCluster(cluster: ProductCluster): StoredProductVector {
    return {
        vectorId: `product-vector:${cluster.id}`,
        candidateProductId: cluster.id,
        categoryRootId: cluster.category_root_id || null,
        categoryRoot: cluster.category_root || null,
        subcategory: cluster.subcategory || null,
        section: cluster.cluster_type,
        visualScore: null,
        brandScore: null,
        textScore: null,
        sellerPatternScore: null,
        priceEvidenceScore: null,
        matchReason: null,
        sourceStatus: cluster.source_status
    };
}

function createRecord(input: UpsertProductVectorInput): StoredProductVector {
    const cluster = clusterById(input.candidateProductId);
    const categoryRoot = input.categoryRoot ?? cluster?.category_root ?? null;
    const subcategory = normalizeSubcategory(input.subcategory ?? cluster?.subcategory ?? null);

    return {
        vectorId: input.vectorId,
        candidateProductId: input.candidateProductId,
        categoryRootId: input.categoryRootId ?? cluster?.category_root_id ?? null,
        categoryRoot,
        subcategory,
        section: input.section ?? cluster?.cluster_type ?? 'similar_style',
        visualScore: input.visualScore ?? null,
        brandScore: input.brandScore ?? null,
        textScore: input.textScore ?? null,
        sellerPatternScore: input.sellerPatternScore ?? null,
        priceEvidenceScore: input.priceEvidenceScore ?? null,
        matchReason: input.matchReason ?? null,
        sourceStatus: input.sourceStatus ?? cluster?.source_status ?? 'mock'
    };
}

function vectorIntent(vectorId: string, context: CategoryContext): string {
    const normalized = vectorId.toLowerCase();
    if (/shoes?|sneakers?|loafer|boots?/.test(normalized)) return 'shoes';
    if (/bags?|pouch|tote/.test(normalized)) return 'bag';
    if (/bottom|pants?|denim|jeans?|cargo|slacks|trousers?/.test(normalized)) return 'bottom';
    if (/top|blouse|shirt|cardigan|knit|liette/.test(normalized)) return 'top';
    return categoryBaseId(context.selectedCategoryId) || 'unknown';
}

function textMatchesHint(text: string | null | undefined, hints: ImageEmbeddingSearchHint[]): boolean {
    const normalizedText = nonEmptyText(text).toLowerCase();
    if (!normalizedText) return false;

    return hints.some((hint) => {
        const normalizedHint = nonEmptyText(hint.normalizedText || hint.text).toLowerCase();
        return Boolean(normalizedHint && normalizedText.includes(normalizedHint));
    });
}

function bestHintConfidence(hints: ImageEmbeddingSearchHint[]): number {
    return hints.reduce((best, hint) => {
        const confidence = typeof hint.confidence === 'number' ? hint.confidence : 0.5;
        return Math.max(best, confidence);
    }, 0);
}

function visualScoreForRecord(record: StoredProductVector, intent: string): number {
    if (typeof record.visualScore === 'number') return clampScore(record.visualScore);
    const profile = vectorIntentVisualScores[intent] || {};
    return clampScore(profile[record.candidateProductId] ?? 0.42);
}

function defaultBrandScore(record: StoredProductVector): number {
    if (typeof record.brandScore === 'number') return clampScore(record.brandScore);
    if (record.section === 'brand_exact') return 0.58;
    if (record.section === 'wholesale_same') return 0.48;
    return 0.18;
}

function defaultTextScore(record: StoredProductVector): number {
    if (typeof record.textScore === 'number') return clampScore(record.textScore);
    if (record.section === 'wholesale_same') return 0.5;
    if (record.section === 'brand_exact') return 0.36;
    return 0.22;
}

function sellerPatternScoreForRecord(record: StoredProductVector): number {
    if (typeof record.sellerPatternScore === 'number') return clampScore(record.sellerPatternScore);
    if (record.section === 'wholesale_same') return 0.74;
    if (record.section === 'brand_exact') return 0.62;
    return 0.24;
}

function priceEvidenceScoreForRecord(record: StoredProductVector): number {
    if (typeof record.priceEvidenceScore === 'number') return clampScore(record.priceEvidenceScore);
    if (record.section === 'wholesale_same') return 0.58;
    if (record.section === 'brand_exact') return 0.56;
    return 0.28;
}

function brandScoreForRecord(record: StoredProductVector, context: CategoryContext): number {
    const cluster = clusterById(record.candidateProductId);
    const brandHints = context.brandHints.concat(context.wholesaleTagHints);
    const hintMatched = Boolean(
        cluster &&
        (
            textMatchesHint(cluster.brand_name, brandHints) ||
            textMatchesHint(cluster.wholesale_tag_name, brandHints) ||
            textMatchesHint(cluster.canonical_name, brandHints)
        )
    );

    return clampScore(Math.max(defaultBrandScore(record), hintMatched ? bestHintConfidence(brandHints) : 0));
}

function textScoreForRecord(record: StoredProductVector, context: CategoryContext): number {
    const cluster = clusterById(record.candidateProductId);
    const hintMatched = Boolean(
        cluster &&
        (
            textMatchesHint(cluster.canonical_name, context.ocrHints) ||
            textMatchesHint(cluster.brand_name, context.ocrHints) ||
            textMatchesHint(cluster.wholesale_tag_name, context.ocrHints)
        )
    );

    return clampScore(Math.max(defaultTextScore(record), hintMatched ? bestHintConfidence(context.ocrHints) : 0));
}

function hintEvidenceMatched(record: StoredProductVector, context: CategoryContext, kind: 'brand' | 'ocr'): boolean {
    const cluster = clusterById(record.candidateProductId);
    if (!cluster) return false;

    const hints = kind === 'brand'
        ? context.brandHints.concat(context.wholesaleTagHints)
        : context.ocrHints;

    return textMatchesHint(cluster.canonical_name, hints) ||
        textMatchesHint(cluster.brand_name, hints) ||
        textMatchesHint(cluster.wholesale_tag_name, hints);
}

function categoryScoreForRecord(record: StoredProductVector, context: CategoryContext): CategoryScore {
    if (!context.categoryRoot) {
        return {
            categoryScore: 0.72,
            subcategoryScore: 0.72,
            categoryMatched: true,
            subcategoryMatched: null
        };
    }

    if (record.categoryRoot !== context.categoryRoot) {
        return {
            categoryScore: 0.05,
            subcategoryScore: 0.05,
            categoryMatched: false,
            subcategoryMatched: false
        };
    }

    if (!context.subcategory) {
        return {
            categoryScore: 1,
            subcategoryScore: 0.72,
            categoryMatched: true,
            subcategoryMatched: null
        };
    }

    const allowedSubcategories = compatibleExactSubcategories(context.subcategory);
    const candidateSubcategory = normalizeSubcategory(record.subcategory);
    if (candidateSubcategory && allowedSubcategories?.has(candidateSubcategory)) {
        return {
            categoryScore: 1,
            subcategoryScore: 1,
            categoryMatched: true,
            subcategoryMatched: true
        };
    }

    if (candidateSubcategory) {
        return {
            categoryScore: 1,
            subcategoryScore: 0.18,
            categoryMatched: true,
            subcategoryMatched: false
        };
    }

    return {
        categoryScore: 1,
        subcategoryScore: context.subcategoryConfirmed ? 0.54 : 0.72,
        categoryMatched: true,
        subcategoryMatched: null
    };
}

function matchReasonForRecord(input: {
    record: StoredProductVector;
    section: ProductClusterType;
    categoryMatched: boolean;
    subcategoryMatched: boolean | null;
}): string {
    if (input.record.matchReason) return input.record.matchReason;
    if (!input.categoryMatched) return '카테고리 불일치로 동일/도매택 후보에서 제외한 vector 후보';
    if (input.subcategoryMatched === false) return '하위 카테고리 불일치로 유사 디자인에만 둔 vector 후보';
    if (input.section === 'brand_exact') return '선택 crop vector, 카테고리, 브랜드 보조 신호를 함께 만족한 후보';
    if (input.section === 'wholesale_same') return '선택 crop vector, 카테고리, 도매택/텍스트 보조 신호를 함께 만족한 후보';
    return '동일 상품 확정 근거는 부족하지만 시각 참고 가치가 있는 vector 후보';
}

function shouldKeepCandidate(input: {
    context: CategoryContext;
    categoryMatched: boolean;
    finalScore: number;
}): boolean {
    if (!input.categoryMatched) {
        return input.context.includeMismatchedCandidates && input.finalScore >= 0.3;
    }
    return input.finalScore >= 0.5;
}

function sourceStatusForCandidates(candidates: VectorSearchCandidate[]): SourceStatus {
    if (candidates.length === 0) return 'api_pending';
    if (candidates.some((candidate) => candidate.sourceStatus === 'needs_verification')) return 'needs_verification';
    if (candidates.some((candidate) => candidate.sourceStatus === 'mock')) return 'mock';
    if (candidates.some((candidate) => candidate.sourceStatus === 'api_pending')) return 'api_pending';
    return 'image_match_ready';
}

function sectionsForCandidates(candidates: VectorSearchCandidate[]): VectorSearchResultSection[] {
    return sectionOrder.map((section) => ({
        section,
        label: sectionLabel(section),
        candidates: candidates.filter((candidate) => candidate.section === section)
    }));
}

export function toImageEmbeddingSearchCandidate(candidate: VectorSearchCandidate): ImageEmbeddingSearchCandidate {
    return {
        candidateProductId: candidate.candidateProductId,
        section: candidate.section,
        visualScore: candidate.visualScore,
        categoryScore: candidate.categoryScore,
        subcategoryScore: candidate.subcategoryScore,
        brandScore: candidate.brandScore,
        textScore: candidate.textScore,
        sellerPatternScore: candidate.sellerPatternScore,
        priceEvidenceScore: candidate.priceEvidenceScore,
        finalScore: candidate.finalScore,
        matchReason: candidate.matchReason,
        sourceStatus: candidate.sourceStatus,
        categoryMatched: candidate.categoryMatched,
        subcategoryMatched: candidate.subcategoryMatched
    };
}

export function vectorSearchResponseToImageEmbeddingSearchResponse(
    response: VectorSearchResponse,
    input: {
        sourceRequestId: string;
        selectedCategoryId: LookitItemCategoryId | string | null;
    }
): ImageEmbeddingSearchResponse {
    const candidates = response.candidates.map(toImageEmbeddingSearchCandidate);
    const sections: ImageEmbeddingSearchResultSection[] = sectionOrder.map((section) => ({
        section,
        label: sectionLabel(section),
        candidates: candidates.filter((candidate) => candidate.section === section)
    }));

    return {
        sourceRequestId: input.sourceRequestId,
        selectedCategoryId: input.selectedCategoryId,
        sourceStatus: response.sourceStatus,
        candidates,
        sections
    };
}

export class MockVectorSearchRepository implements VectorSearchRepository {
    private readonly records = new Map<string, StoredProductVector>();

    constructor(initialRecords: UpsertProductVectorInput[] | null = null) {
        const records = initialRecords ?? lookitProductClusters.map(defaultRecordForCluster);
        records.forEach((record) => {
            const stored = 'vectorId' in record && 'candidateProductId' in record
                ? createRecord(record)
                : record as StoredProductVector;
            this.records.set(stored.vectorId, cloneData(stored));
        });
    }

    async searchSimilarProducts(
        vectorIdInput: string,
        categoryFilter: VectorSearchCategoryFilter,
        limit = 20
    ): Promise<VectorSearchResponse> {
        const vectorId = nonEmptyText(vectorIdInput);
        if (!vectorId) throw new Error('vectorId is required for vector search');

        const context = normalizeFilter(categoryFilter);
        const intent = vectorIntent(vectorId, context);
        const candidates = Array.from(this.records.values())
            .map((record) => {
                const visualScore = visualScoreForRecord(record, intent);
                const categoryScore = categoryScoreForRecord(record, context);
                const brandScore = brandScoreForRecord(record, context);
                const textScore = textScoreForRecord(record, context);
                const sellerPatternScore = sellerPatternScoreForRecord(record);
                const priceEvidenceScore = priceEvidenceScoreForRecord(record);
                const cluster = clusterById(record.candidateProductId);
                const ranking = rankSearchCandidate({
                    requestedSection: record.section,
                    visualScore,
                    categoryScore: categoryScore.categoryScore,
                    subcategoryScore: categoryScore.subcategoryScore,
                    brandScore,
                    textScore,
                    sellerPatternScore,
                    priceEvidenceScore,
                    categoryMatched: categoryScore.categoryMatched,
                    subcategoryMatched: categoryScore.subcategoryMatched,
                    hasBrandEvidence: Boolean(
                        hintEvidenceMatched(record, context, 'brand') ||
                        (record.section === 'brand_exact' && cluster?.brand_name) ||
                        typeof record.brandScore === 'number'
                    ),
                    hasTextEvidence: Boolean(
                        hintEvidenceMatched(record, context, 'ocr') ||
                        typeof record.textScore === 'number'
                    ),
                    hasSellerPatternEvidence: Boolean(
                        sellerPatternScore >= 0.55 &&
                        (record.section === 'wholesale_same' || record.section === 'brand_exact' || typeof record.sellerPatternScore === 'number')
                    ),
                    hasPriceEvidence: Boolean(
                        priceEvidenceScore >= 0.55 &&
                        (record.section === 'wholesale_same' || record.section === 'brand_exact' || typeof record.priceEvidenceScore === 'number')
                    ),
                    sourceStatus: record.sourceStatus
                });
                const finalScore = ranking.finalScore;
                const section = ranking.section;

                if (!shouldKeepCandidate({
                    context,
                    categoryMatched: categoryScore.categoryMatched,
                    finalScore
                })) {
                    return null;
                }

                const candidate: VectorSearchCandidate = {
                    queryVectorId: vectorId,
                    productVectorId: record.vectorId,
                    candidateProductId: record.candidateProductId,
                    section,
                    visualScore,
                    categoryScore: categoryScore.categoryScore,
                    subcategoryScore: categoryScore.subcategoryScore,
                    brandScore,
                    textScore,
                    sellerPatternScore,
                    priceEvidenceScore,
                    finalScore,
                    matchReason: matchReasonForRecord({
                        record,
                        section,
                        categoryMatched: categoryScore.categoryMatched,
                        subcategoryMatched: categoryScore.subcategoryMatched
                    }),
                    sourceStatus: ranking.sourceStatus,
                    categoryMatched: categoryScore.categoryMatched,
                    subcategoryMatched: categoryScore.subcategoryMatched
                };

                return candidate;
            })
            .filter((candidate): candidate is VectorSearchCandidate => Boolean(candidate))
            .sort((a, b) => {
                const sectionDiff = sectionOrder.indexOf(a.section) - sectionOrder.indexOf(b.section);
                if (sectionDiff !== 0) return sectionDiff;
                const scoreDiff = b.finalScore - a.finalScore;
                if (scoreDiff !== 0) return scoreDiff;
                return a.candidateProductId.localeCompare(b.candidateProductId);
            })
            .slice(0, limit)
            .map((candidate) => cloneData(candidate));

        return {
            vectorId,
            selectedCategoryId: context.selectedCategoryId,
            sourceStatus: sourceStatusForCandidates(candidates),
            candidates,
            sections: sectionsForCandidates(candidates)
        };
    }

    async upsertProductVector(input: UpsertProductVectorInput): Promise<UpsertProductVectorResult> {
        const vectorId = nonEmptyText(input.vectorId);
        const candidateProductId = nonEmptyText(input.candidateProductId);
        if (!vectorId) throw new Error('vectorId is required for product vector upsert');
        if (!candidateProductId) throw new Error('candidateProductId is required for product vector upsert');

        const record = createRecord({
            ...input,
            vectorId,
            candidateProductId
        });
        this.records.set(record.vectorId, cloneData(record));

        return {
            ok: true,
            vectorId: record.vectorId,
            candidateProductId: record.candidateProductId,
            sourceStatus: record.sourceStatus
        };
    }

    async deleteVector(vectorIdInput: string): Promise<DeleteVectorResult> {
        const vectorId = nonEmptyText(vectorIdInput);
        if (!vectorId) throw new Error('vectorId is required for product vector delete');

        const deleted = this.records.delete(vectorId);
        return {
            ok: true,
            vectorId,
            status: deleted ? 'deleted' : 'no_records',
            deletedAt: new Date().toISOString()
        };
    }
}

export const mockVectorSearchRepository = new MockVectorSearchRepository();
